// ... existing imports
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from "@mui/material";
import { startCase } from "lodash";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { ViewColumn } from "@mui/icons-material";
import LaydownList from "../LaydownList";
import { fetchInventoryData } from "../../../atoms/logistics/LogisticsManagementAtom";
import { useAtom } from "jotai";
import ParentTable from "./ParentTable";
import axiosInstance from "../../../utils/axios";
import settingsAtom from "../../../atoms/settings/SettingsAtom";
import { exportInventoryToExcel } from "./constent";
import { getCheckedColumns, setCheckedColumns, getColumnStorageKey } from '../../../shared/columnSelection';
import { isReadOnly } from "../../../utils/permissionUtils";

const LOCAL_STORAGE_KEY = getColumnStorageKey('inventory');
const defaultVisible = [
  'part_name', 'description', 'requiredQty', 'deliveredQty',
  'distributedQty', 'installedQty', 'currentQty'
];

const allColumns = [
  { key: 'part_name', label: 'Part Number' },
  { key: 'description', label: 'Part Description' },
  { key: 'requiredQty', label: 'Required Qty' },
  { key: 'deliveredQty', label: 'Delivered Qty' },
  { key: 'distributedQty', label: 'Distributed Qty' },
  { key: 'installedQty', label: 'Installed Qty' },
  { key: 'currentQty', label: 'Current Qty' },
  { key: 'damagedQty', label: 'Damaged in Delivery' },
  { key: 'DamagedInConstruction', label: 'Damaged in Construction' },
  { key: 'TotalDamaged', label: 'Total Damaged' },
  { key: 'remediateQty', label: 'Remediate Qty' },
  { key: 'DeficitQty', label: 'Deficit Qty' },
];

const childHeaders = [
  { key: "date_time", label: 'Date & Time' },
  { key: "delivery_reference", label: 'Delivery Reference' },
  { key: 'deliveredQty', label: 'Delivered Qty' },
  { key: "quantity", label: "Quantity" },
  { key: "action", label: "Action" },
  { key: "comment", label: "Comment" }
];

const InventoryComponent = ({ data, title, laydownList, current, setCurrent, setLaydown, aliasName }) => {
  const [visible, setVisible] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState(() => {
    const stored = getCheckedColumns(LOCAL_STORAGE_KEY);
    return stored.length ? stored : defaultVisible;
  });
  const [, setInventory] = useAtom(fetchInventoryData);
  const [loader, setLoader] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const [settingsData] = useAtom(settingsAtom);
  const { projectList } = settingsData;
  const projectInfo = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));

  const [searchPartName, setSearchPartName] = useState('');
  const [searchDescription, setSearchDescription] = useState('');
  const [filteredData, setFilteredData] = useState(data || []);
  const [searchLoader, setSearchLoader] = useState(false);
  const lastFetchedLaydownIdRef = useRef(null);
  const [openRowIndex, setOpenRowIndex] = useState(null);

  const handleToggle = (key) => {
    setVisibleCols((prev) => {
      const updated = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      setCheckedColumns(LOCAL_STORAGE_KEY, updated);
      return updated;
    });
  };

  const fetchData = async (id) => {
    if (!id || id === lastFetchedLaydownIdRef.current) return;
    try {
      setLoader(true);
      await setInventory(id);
      lastFetchedLaydownIdRef.current = id;
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const search = () => {
      setSearchLoader(true);
      const lowerPart = searchPartName.toLowerCase();
      const lowerDesc = searchDescription.toLowerCase();

      const result = data?.filter(item =>
        item.part_name?.toLowerCase().includes(lowerPart) &&
        item.description?.toLowerCase().includes(lowerDesc)
      ) || [];

      setFilteredData(result);
      setSearchLoader(false);
    };

    search();
  }, [searchPartName, searchDescription, data]);

  useEffect(() => {
    setFilteredData(data || []);
  }, [data]);

  useEffect(() => {
    if (current?.id) {
      fetchData(current.id);
    }
    // eslint-disable-next-line
  }, [current?.id]);

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoader(true);
      await axiosInstance.delete(`projects/laydown?laydown_id=${current?.id}`);
      await setLaydown();
    } catch (error) {
      console.error("Error deleting laydown:", error);
    } finally {
      setDeleteModalOpen(false);
      setDeleteLoader(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloadLoader(true);
      if (!data || data.length === 0) return;

      const projectDetails = {
        projectNumber: projectInfo?.project_number || 'N/A',
        projectName: projectInfo?.name || 'Project Name',
        reportTitle: current?.name || 'Inventory Report',
        reportDate: new Date().toLocaleDateString()
      };

      await new Promise(resolve => setTimeout(resolve, 300));
      exportInventoryToExcel(data, projectDetails, allColumns, childHeaders);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadLoader(false);
    }
  };

  return (
    <Box sx={{ position: "relative" }} >
      <Box sx={{ display: "flex", width: "100%" }}>
        <Box pr={2} sx={{ width: visible ? "80%" : "100%", transition: "width 0.3s ease" }}>
          <Box mb={1} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Typography fontSize={20} fontWeight={600}>
              {startCase(title)}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Search Part Number"
                value={searchPartName}
                onChange={(e) => setSearchPartName(e.target.value)}
                sx={{
                  width: 240,
                  '& .MuiInputBase-root': { height: 45 },
                  '& .MuiInputLabel-root': { fontSize: '18px' } 
                }}
              />
              <TextField
                size="small"
                label="Search Description"
                value={searchDescription}
                onChange={(e) => setSearchDescription(e.target.value)}
                sx={{
                  width: 240,
                  '& .MuiInputBase-root': { height: 45 },
                  '& .MuiInputLabel-root': { fontSize: '18px' } 
                }}
              />
              <Button
                variant="outlined"
                startIcon={downloadLoader ? <CircularProgress size={16} /> : <Download size={16} />}
                onClick={handleDownload}
                disabled={downloadLoader || !data?.length || isReadOnly()}
              >
                {downloadLoader ? 'Downloading...' : 'Download'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ViewColumn />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ mr: 4 }}
              >
                Columns
              </Button>
            </Box>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box p={2} sx={{ display: 'flex', flexDirection: 'column', fontSize: "16px" }}>
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={visibleCols.includes(col.key)}
                        onChange={() => handleToggle(col.key)}
                      />
                    }
                    label={<Box fontSize={16}>{col.label}</Box>}
                  />
                ))}
              </Box>
            </Popover>
          </Box>

          <ParentTable
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            visibleCols={visibleCols}
            setVisibleCols={setVisibleCols}
            allColumns={allColumns}
            handleToggle={handleToggle}
            rows={filteredData}
            laydownList={laydownList}
            current={current}
            aliasName={aliasName}
            loading={loader || searchLoader}
            openRowIndex={openRowIndex}
            setOpenRowIndex={setOpenRowIndex} 
          />
        </Box>

        {visible && (
          <Box sx={{ width: "20%", height: "calc(100vh - 200px)", borderLeft: "1px solid #ccc", paddingLeft: 2 }}>
            <LaydownList
              currentLaydown={current}
              setCurrentLaydown={setCurrent}
              laydownList={laydownList}
              setOpenRowIndex={setOpenRowIndex}
            />
          </Box>
        )}

        <Box
          onClick={() => setVisible(!visible)}
          sx={{
            height: "24px",
            border: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            zIndex: 10,
            position: "absolute",
            top: 0,
            right: visible ? "20%" : 0,
            width: "24px",
            cursor: "pointer",
            transition: "right 0.3s ease"
          }}
        >
          {visible ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </Box>
      </Box>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={600}>Confirm Delete Laydown</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are deleting the laydown with some current quantities and/or damaged quantities reported.
            This deletion will modify the initial delivery records accordingly.
            You may download the inventory log for your reference if required.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={deleteLoader} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoader}
            startIcon={deleteLoader ? <CircularProgress size={16} /> : null}
          >
            {deleteLoader ? "Deleting..." : "Understood, proceed with deletion"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryComponent;
